import { Link } from 'react-router-dom';
import type { Event } from '@wineryxplorer/shared';
import { ManagementRowActions } from '../ui/ManagementRowActions';

// One event in the venue-events manager. Same row grammar as
// `venueWines/VenueWineRow.tsx` and the public `components/WineRow.tsx` —
// leading gutter value, serif name linking to the detail page, dot-joined
// sub-line, hairline rule between rows — so the management lists read as one
// product rather than as a generic card dashboard (#349). The parent still
// owns the query, the delete mutation and its confirm dialog.
interface VenueEventRowProps {
  event: Event;
  isDeleting: boolean;
  onEdit: (eventId: string) => void;
  onDelete: (eventId: string) => void;
}

// The gutter is the event's date, which is what a venue admin scans this list
// by. The year only earns its space when the event is not in the current one,
// which keeps the common case narrow but stops the Past tab from collapsing
// several years' events into indistinguishable day-and-month pairs.
const formatGutterDate = (startDate: Date): string => {
  const date = new Date(startDate);
  if (Number.isNaN(date.getTime())) return '—';

  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const formatTime = (startTime?: { hours: number; minutes: number }): string | undefined => {
  if (!startTime) return undefined;

  const period = startTime.hours >= 12 ? 'PM' : 'AM';
  const displayHours = startTime.hours % 12 === 0 ? 12 : startTime.hours % 12;
  return `${displayHours}:${startTime.minutes.toString().padStart(2, '0')} ${period}`;
};

// Every EVENT_TYPES value is a single lowercase word, so capitalising is the
// whole of the formatting job.
const formatEventType = (eventType: string): string =>
  eventType.charAt(0).toUpperCase() + eventType.slice(1);

// `price` cannot be tested for truthiness: the Event schema defaults
// `price.currency` to 'USD', so Mongoose materialises the subdocument for a
// free event too and hands back `{ currency: 'USD' }` with no amount. The
// amount is the only field worth asking about.
const formatPrice = (price: Event['price']): string => {
  const amount = price?.amount;
  if (amount === undefined || !Number.isFinite(amount)) return '';
  return `$${Math.round(amount)}`;
};

export const VenueEventRow = ({ event, isDeleting, onEdit, onDelete }: VenueEventRowProps) => {
  // The venue and the date are already on the row, so the sub-line carries what
  // distinguishes one of its events from another on the same day.
  const subLine = [
    formatTime(event.startTime),
    event.duration ? `${event.duration} min` : undefined,
    formatEventType(event.eventType),
  ]
    .filter((part): part is string => !!part)
    .join(' · ');

  return (
    <div className="flex items-center gap-4 border-b border-gray-200 px-4 py-3.5 transition-colors last:border-b-0 hover:bg-gray-100">
      <span className="min-w-[76px] flex-shrink-0 text-base text-gray-600">
        {formatGutterDate(event.startDate)}
      </span>

      <Link to={`/events/${event.id}`} className="group min-w-0 flex-1">
        <div className="truncate font-serif text-lg leading-tight text-gray-900 group-hover:text-primary-700">
          {event.name}
        </div>
        {subLine && <div className="mt-0.5 truncate text-sm text-gray-600">{subLine}</div>}
      </Link>

      <span className="min-w-[60px] flex-shrink-0 text-right text-base text-gray-700">
        {formatPrice(event.price)}
      </span>

      <ManagementRowActions
        itemId={event.id}
        itemName={event.name}
        isDeleting={isDeleting}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
};
